"use client"
import React, { useState, useEffect } from "react";
import useApi from "@/components/api/useApi";
import { Formik } from "formik";
import { Form, FormGroup, FormControl } from "react-bootstrap";
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import LocationSelector from "@/components/shared/LocationSelector";
import NewFileUpload from "@/components/content/NewFileUpload";
import UrlInput from "@/components/shared/UrlInput";
import MapLayerSelector from "@/components/maps/MapLayerSelector";
import ReactDOM from "react-dom";

import "@/components/maps/MapPointForm.css";
import MaxCharacterField from "@/components/shared/MaxCharacterField";
import TagSelector from "@/components/shared/TagSelector";
import DOMPurify from "dompurify";
import { useCurrentLocation } from "@/lib/providers/GeolocationProvider";

let yup = require("yup");

const MapPointForm = ({
  name,
  authentication,
  mapId,
  pointId,
  point,
  onPointSubmitted,
  onPointAdded,
  onPointEdited,
  onMovePoint,
  layers,
  defaultLayer,
  onCancel,
  submission,
  modalFooter,
  onSubmitOverride,
  onDeferredSubmission
}) => {
  const [metadata, setMetadata] = useState(null);
  const [deferredFileUploads, setDeferredFileUploads] = useState([]);

  const api = useApi();
  const randomNumber = api.getRandomInt(10000, 99999);

  const transactionId = btoa(`${name}:${mapId}`);

  const { currentLocation } = useCurrentLocation();

  useEffect(() => {
    if (point && point.metadata && !metadata) {
      setMetadata(JSON.parse(point.metadata));
    }
  }, [point]);

  const submitPoint = values => {
    var payload = values;
    payload["type"] = "STORY";

    if (onDeferredSubmission) {
      onDeferredSubmission(values, deferredFileUploads);
    } else {
      // the API expects the owner field to contain the owner's name and not an object
      values.assets.forEach(function(asset, index) {
        if (asset.owner) {
          asset.owner = asset.owner.resourceName;
        }
      });
      if (metadata && payload.url !== "") {
        payload["metadata"] = JSON.stringify(metadata);
      }
      if (submission) {
        payload["external"] = true;
        if (authentication) {
          payload["ownerResourceName"] = authentication.profileName;
        }
        api
          .submitProfileMapPoint(name, mapId, payload)
          .then(success => {
            onPointSubmitted();
          })
          .catch(error => {});
      } else {
        if (onSubmitOverride) {
          onSubmitOverride(pointId, payload);
        } else {
          if (pointId != null) {
            api
              .updateExistingMapPoint(name, mapId, pointId, payload)
              .then(resp => {
                window.flash_messages.addMessage({
                  id: "id",
                  text: "Map Pin Saved!",
                  type: "success"
                });
                payload["id"] = pointId;
                onPointEdited(payload);
              })
              .catch(error => {
                window.flash_messages.addMessage({
                  id: "id",
                  text: "Something went wrong saving map pin.",
                  type: "error"
                });
              });
          } else {
            api
              .createNewMapPoint(name, mapId, payload)
              .then(resp => {
                if (resp.success === true) {
                  let mid = resp.id;
                  window.flash_messages.addMessage({
                    id: "id",
                    text: "Map Pin Saved!",
                    type: "success"
                  });
                  payload["id"] = mid;
                  onPointAdded(payload);
                } else {
                  // do I ever get here?
                }
              })
              .catch(error => {
                window.flash_messages.addMessage({
                  id: "id",
                  text: "Something went wrong saving map pin.",
                  type: "error"
                });
              });
          }
        }
      }
    }
  };

  const updateLocationOnAsset = (assets, setFieldValue) => {
    if (assets.length > 0) {
      const firstAsset = assets[0];
      if (firstAsset.approxLocation) {
        setFieldValue("location", firstAsset.approxLocation);
      }
    }
  };

  // RENDER

  var schema = yup.object({
    title: yup
      .string()
      .required()
      .label("Title"),
    text: yup
      .string()
      .nullable()
      .label("Text"),
    location: yup
      .object()
      .required()
      .label("Location"),
    displayLocation: yup.boolean(),
    url: yup
      .string()
      .nullable()
      .url("Must be a valid URL (https://example.com)")
      .label("URL"),
    pinText: yup
      .string()
      .nullable()
      .max(3)
      .label("Pin Text")
  });

  let values = {
    owner: authentication ? authentication.profileName : name,
    title: point ? point.title : "",
    text: point ? point.text : "",
    location: point ? point.location : currentLocation,
    url: point ? point.url : "",
    displayLocation: point ? point.displayLocation : false,
    pinText: point ? point.pinText : "",
    tags: point ? point.tags ?? [] : [],
    assets: point ? point.assets ?? [] : []
  };

  if (layers) {
    values["layer"] = point ? point.layer ?? defaultLayer ?? null : null;
  }

  if (!authentication) {
    values["external"] = true;
  }

  return (
    <Formik
      initialValues={values}
      enableReinitialize={true}
      validationSchema={schema}
      onSubmit={(values, { setSubmitting }) => {
        setTimeout(() => {
          submitPoint(values);
          setSubmitting(false);
        }, 400);
      }}
    >
      {({
        handleSubmit,
        handleChange,
        handleBlur,
        values,
        touched,
        isValid,
        isSubmitting,
        errors,
        setFieldValue
      }) => {
        // console.log(values, touched, errors, isValid);
        return (
          <div>
            <div style={{ width: "100%" }}>
              <Form
                id={`map-point-form-${randomNumber}`}
                noValidate
                className="no-style"
                onSubmit={handleSubmit}
              >
                <Container>
                  {submission && (
                    <Row>
                      <div className="instructional-text">
                        <p>
                          Give us information about the pin you want to
                          contribute to this map. Fields marked with * are
                          required.
                        </p>
                      </div>
                    </Row>
                  )}
                  <Row>
                    <Col>
                      <div className="form-group" id="validationFormikMedia">
                        <Form.Label>Media</Form.Label>
                        <NewFileUpload
                          upload={true}
                          show={true}
                          name={
                            authentication ? authentication.profileName : null
                          }
                          deferred={submission}
                          multiple={true}
                          onSelect={(assets, files) => {
                            // console.log("MapPointForm onSelect", assets, files);
                            if (submission && files) {
                              setDeferredFileUploads(files);
                            }
                            if (!submission && !touched.location) {
                              updateLocationOnAsset(assets, setFieldValue);
                            }
                            setFieldValue("assets", assets);
                          }}
                          selectedAssets={values.assets}
                          transactionId={transactionId}
                          filterEnabled={authentication != null}
                          types={
                            submission ? ["IMAGE"] : ["IMAGE", "AUDIO", "VIDEO"]
                          }
                        />
                      </div>
                      <div className="form-group" id="validationFormikLocation">
                        <Form.Label bsPrefix="form-label required">
                          Location
                        </Form.Label>
                        <LocationSelector
                          isMulti={false}
                          location={values.location}
                          returnFullLocation={true}
                          label="name"
                          isInvalid={!!errors.location && touched.location}
                          onSelect={(selectedOption, place) => {
                            setFieldValue("location", place);
                            if (
                              place.locationSource === "URALITY_PROFILE" &&
                              place.userProfile &&
                              place.userProfile.coverPhotoId
                            ) {
                              var newAssets = [...values.assets];
                              newAssets.unshift({
                                id: place.userProfile.coverPhotoId,
                                assetType: "IMAGE"
                              });
                              setFieldValue("assets", newAssets);
                            }
                          }}
                          profileSearch={true}
                          name={name}
                          moveEnabled={pointId && onMovePoint}
                          onMovePoint={() => onMovePoint()}
                        />
                      </div>
                    </Col>
                  </Row>
                  <Row>
                    <Col>
                      <>
                        <div className="form-group" id="validationFormikTitle">
                          <Form.Label bsPrefix="form-label required">
                            Title
                          </Form.Label>
                          <FormControl
                            name="title"
                            value={values.title}
                            onChange={e => {
                              const cleanValue = DOMPurify.sanitize(
                                e.target.value,
                                {
                                  ALLOWED_TAGS: []
                                }
                              );
                              setFieldValue("title", cleanValue);
                            }}
                            onBlur={handleBlur}
                            isInvalid={!!errors.title && touched.title}
                            placeholder="Title"
                          />
                          <Form.Control.Feedback type="invalid">
                            {errors.title}
                          </Form.Control.Feedback>
                        </div>
                        <div className="form-group" id="validationFormikText">
                          <Form.Label>Text</Form.Label>
                          <FormControl
                            as="textarea"
                            rows={4}
                            name="text"
                            value={values.text}
                            onChange={e => {
                              const cleanValue = DOMPurify.sanitize(
                                e.target.value,
                                {
                                  ALLOWED_TAGS: []
                                }
                              );
                              setFieldValue("text", cleanValue);
                            }}
                            onBlur={handleBlur}
                            isInvalid={!!errors.text && touched.text}
                            placeholder="Enter text here"
                          />
                        </div>
                        {layers && layers.length > 0 && (
                          <div
                            className="form-group"
                            id="validationFormikLayer"
                          >
                            <MapLayerSelector
                              isClearable={true}
                              allowAll={false}
                              layers={layers}
                              selected={values.layer}
                              onSelect={value => setFieldValue("layer", value)}
                              label="Layer"
                            />
                          </div>
                        )}
                      </>
                    </Col>
                  </Row>

                  {!submission && (
                    <Row>
                      <Col>
                        <div className="form-group" id="validationFormikTags">
                          <Form.Label>Tags</Form.Label>
                          <TagSelector
                            onSelect={tags => setFieldValue("tags", tags)}
                            onBlur={handleBlur}
                            defaultValue={values.tags}
                          />
                        </div>
                        {values.location &&
                          values.location.locationSource !==
                            "URALITY_PROFILE" && (
                            <>
                              <UrlInput
                                name="url"
                                value={values.url}
                                metadata={metadata}
                                onUrlChange={(url, metadata) => {
                                  setFieldValue("url", url ?? null);
                                  setMetadata(metadata ?? null);
                                }}
                                onBlur={handleBlur}
                              />
                              <div
                                className="form-group"
                                id="validationFormikPinText"
                              >
                                <MaxCharacterField
                                  title="Pin Text"
                                  schema={schema}
                                  value={values.pinText}
                                  fieldName="pinText"
                                  isInvalid={
                                    !!errors.pinText && touched.pinText
                                  }
                                  placeholder="Pin Text"
                                  onBlur={handleBlur}
                                  onChange={handleChange}
                                />
                                <Form.Text>
                                  This text will appear on your pin on the map.
                                </Form.Text>
                              </div>
                              <div
                                className="form-group"
                                id="validationFormikDisplayLocation"
                              >
                                <Form.Check
                                  className={
                                    values.displayLocation ? "checked" : ""
                                  }
                                  custom="true"
                                  type="checkbox"
                                  checked={values.displayLocation}
                                  id="displayLocation"
                                  name="displayLocation"
                                  onChange={evt => {
                                    var enabled = evt.target.checked;
                                    setFieldValue("displayLocation", enabled);
                                  }}
                                  label="Display location link on card"
                                />
                              </div>
                            </>
                          )}
                      </Col>
                    </Row>
                  )}

                  {modalFooter ? (
                    <>
                      {ReactDOM.createPortal(
                        <>
                          <button
                            form={`map-point-form-${randomNumber}`}
                            type="submit"
                            disabled={!isValid || isSubmitting}
                            className="btn btn-primary"
                          >
                            {submission && !authentication ? "Next" : "Save"}
                          </button>
                          <button
                            type="button"
                            className="btn btn-cancel"
                            onClick={() => onCancel()}
                          >
                            Cancel
                          </button>
                        </>,
                        modalFooter
                      )}
                    </>
                  ) : (
                    <Row style={{ marginTop: "1rem" }}>
                      <Col>
                        <button
                          form={`map-point-form-${randomNumber}`}
                          type="submit"
                          disabled={!isValid || isSubmitting}
                          className="btn btn-primary"
                        >
                          Save
                        </button>
                        <button
                          type="button"
                          className="btn btn-cancel"
                          onClick={() => onCancel()}
                        >
                          Cancel
                        </button>
                      </Col>
                    </Row>
                  )}
                </Container>
              </Form>
            </div>
          </div>
        );
      }}
    </Formik>
  );
};
export default MapPointForm;
